import React, { memo, KeyboardEvent } from 'react'
import { MESSAGE_TYPES, IGlobalMessage, IGlobalMessageCta } from 'types/globalMessaging'
import { CloseIcon } from 'transfix-icons'
import { KEY_CODES } from 'constants/keyValues'
import ClipboardCopy from '../../Copy'

const getDisplayType = (type: MESSAGE_TYPES) => {
  switch (type) {
    case MESSAGE_TYPES.warning:
      return 'Warning!'
    case MESSAGE_TYPES.alert:
      return 'Alert!'
    default:
      return 'Success!'
  }
}

interface IActionProps extends IGlobalMessageCta {
  onClose: () => void
}

export const Action = memo<IActionProps>(({ action, label, onClose, url }) => {
  const clickHandler = () => {
    if (action) action()
    onClose()
  }
  return url ? (
    <a href={url} target='_blank' rel='noopener noreferrer' data-test='global-message-cta-link'>
      {label}
    </a>
  ) : (
    <button className='btn-text-link' onClick={clickHandler} data-test='global-message-cta-button'>
      {label}
    </button>
  )
})

export interface IHideMessageHandler {
  hideMessage: (id: string) => void
}

interface IGlobalMessageProps extends IGlobalMessage, IHideMessageHandler {}

export const Message = memo<IGlobalMessageProps>(
  ({ hideMessage, id, message, type = MESSAGE_TYPES.info, typeLabel, cta, clickToCopyValue }) => {
    const { action, label = '', url } = cta || {}
    const classes = ['ph5', 'pv2', 'global-message', 'flex', type]
    const onClose = () => hideMessage(id)
    const onKeyUp = ({ keyCode }: KeyboardEvent<HTMLButtonElement>) => {
      if (keyCode === KEY_CODES.ESC) hideMessage(id)
    }
    return (
      <div className={classes.join(' ')}>
        <div className='flex-grow-1'>
          <strong className='mr1'>{typeLabel ?? getDisplayType(type)}</strong>
          <span className='mr1'>{message}</span>
          {clickToCopyValue && (
            <ClipboardCopy
              copyText={clickToCopyValue}
              displayText={clickToCopyValue}
              alignClass='bottom'
              useIcon
            />
          )}
          {cta && <Action label={label} url={url} action={action} onClose={onClose} />}
        </div>
        <button
          aria-label='Hide message'
          className='btn-text-link'
          onClick={onClose}
          onKeyUp={onKeyUp}
          data-test='hide-global-message'
          tabIndex={0}>
          <CloseIcon className='hide-icon' />
        </button>
      </div>
    )
  }
)
